import { cx } from "@shared/components/untitledui/util/cx"
import { assignRef } from "@shared/util/assign-ref"
import { tracked } from "@supergrain/kernel/react"
import { generateJSON, type FocusPosition } from "@tiptap/core"
import { Editor, useEditor } from "@tiptap/react"
import type * as filestack from "filestack-js"
import { useEffect, useMemo, useRef, type Ref } from "react"
import { useLinkDialog } from "./editor-ui/link/use-link-dialog"
import { commentTiptapExtensions, headlessCardValueExtensions } from "./extensions"
import type { GoogleDrivePickedFile } from "./extensions/link/link-attrs"
import {
  EMPTY_COMMENT_ACTIONS,
  type DerivedPermissions,
  type UnifiedTiptapUiValue,
} from "./unified-tiptap-ui-context"
import UnifiedTiptapRenderer from "./UnifiedTiptapRenderer"
import { isSubmitShortcut } from "./util/submit-shortcut"

export interface CommentTiptapProps {
  /** What the comment already says, as the HTML it is stored as. Unset composes a new one. */
  initialHtml?: string
  /** Every change, for a caller that follows the comment as it is typed (e.g. for emptiness). */
  onUpdate?: (editor: Editor) => void
  /** Runs on Mod-Enter, the shortcut for submitting the comment. */
  onSubmitShortcut?: () => void
  /** Names the editable region. */
  ariaLabel?: string
  autofocus?: FocusPosition
  editorRef?: Ref<Editor | null>
  /** Extra classes for the editable region. */
  className?: string
  filestackClient?: filestack.Client
  addFromGoogleDrive?: () => Promise<GoogleDrivePickedFile[]>
}

/** A comment is written by whoever opened its editor, so it is always editable. */
const COMMENT_PERMISSIONS: DerivedPermissions = { canEdit: true, canComment: false }

/**
 * Editor for a card comment: the blocks the comment holds, under one Add button.
 *
 * Read the comment's HTML using `readCommentHtml(editor.state)`, which processes it for storing.
 */
export const CommentTiptap = tracked((props: CommentTiptapProps) => {
  const latestPropsRef = useRef(props)
  latestPropsRef.current = props

  const linkDialog = useLinkDialog()

  const createInitialDoc = () => {
    // Parse the stored HTML as blocks using headlessCardValueExtensions.
    // These are a superset of our allowed block extensions - okay as the stored HTML
    // only uses a comment's block types.
    const blocks =
      generateJSON(props.initialHtml ?? "", headlessCardValueExtensions()).content ?? []
    return { type: "doc", content: [{ type: "commentContent", content: blocks }] }
  }

  const editor = useEditor({
    extensions: commentTiptapExtensions({
      openLinkDialog: linkDialog.open,
      filestackClient: props.filestackClient ?? null,
    }),
    content: createInitialDoc(),
    autofocus: props.autofocus ?? false,
    editorProps: {
      attributes: {
        // Fill EditorContent.
        class: "flex-1",
        ...(props.ariaLabel ? { "aria-label": props.ariaLabel } : {}),
      },
      handleKeyDown(_view, event) {
        const onSubmitShortcut = latestPropsRef.current.onSubmitShortcut
        if (!onSubmitShortcut || !isSubmitShortcut(event)) return false
        event.preventDefault()
        onSubmitShortcut()
        return true
      },
    },
    onUpdate({ editor }) {
      latestPropsRef.current.onUpdate?.(editor)
    },
  })

  useEffect(() => {
    assignRef(props.editorRef, editor)
    return () => assignRef(props.editorRef, null)
  }, [editor])

  const ui: Omit<UnifiedTiptapUiValue, "arbiter" | "setCardOverlayOpen"> = useMemo(
    () => ({
      permissions: COMMENT_PERMISSIONS,
      // TODO: Support AddButton in nested editors
      hideButtons: true,
      commentActions: EMPTY_COMMENT_ACTIONS,
      currentUserId: null,
      filestackClient: props.filestackClient,
      addFromGoogleDrive: props.addFromGoogleDrive,
    }),
    [props.filestackClient, props.addFromGoogleDrive],
  )

  const floatingPortal = useMemo(
    () => document.getElementById("unified-tiptap-floating-portal")!,
    [],
  )

  return (
    <UnifiedTiptapRenderer
      editor={editor}
      unifiedTiptapUiValue={ui}
      linkDialog={linkDialog}
      floatingPortal={floatingPortal}
      className={cx(props.className, "flex flex-col")}
    />
  )
})

export default CommentTiptap
